import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import Select from "react-select";
import { getNodes, getRiskProfile, getProfilerMetadata} from "../api/quontxApi";
import { formateTimeRange, saveToState, loadFromState, resetState } from "../utils";
import ParquetTable from "./ParquetTable";

const ITEMS_PER_PAGE = 100;

const HEADER_NAME_MAP = {
  'w_2o': 'Weight',
  'n_label': 'Node',
  'ns_label': 'NodeSet',
  'nd_label': 'Node',
  'ti': 'Time Interval',
  'w_2o_max': 'Max Weight',
  'materiality_idx': 'Materiality Index',
};

const COLUMNS_TO_HIDE = ['uuid', 'n_id', 'ns_id', 'nd_id'];
const DEFAULT_SORTED_COLUMN = 'w_2o';

const RiskProfile2oFilters = ({ isLoading, propagnateData }) => {
  const propagnateFunctionRef = useRef(propagnateData);

  const [activeNode, setActiveNode] = useState(null);
  const [activeTimestampBucket, setActiveTimestampBucket] = useState(null);
  const [timestampBuckets, setTimestampBuckets] = useState([]);
  const [loadingBuckets, setLoadingBuckets] = useState(false);
  const [statsType, setStatsType] = useState('n_stats');

  // Key to force table reload
  const [tableKey, setTableKey] = useState(0);

  // Node search
  const toOption = (node) => ({ label: node.name, value: node.uuid });

  const fetchEntitiesPage = useCallback(async ({ search, page }) => {
    const res = await getNodes({
      node_type: "NAMED_ENTITY:ORGANISATION",
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });
    return res?.data?.data ?? [];
  }, []);

  const loadNodeOptions = useCallback(
    async (search, _loadedOptions, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const entities = await fetchEntitiesPage({ search: q, page });

      const options = entities.map(toOption);
      const hasMore = entities.length === ITEMS_PER_PAGE;

      return { options, hasMore, additional: { page: page + 1 } };
    },
    [fetchEntitiesPage]
  );

  const nodeAdditional = useMemo(() => ({ page: 1 }), []);

  // Load timestamp buckets
  const loadTimestampBuckets = useCallback(async () => {
    if (!activeNode?.value) return;

    setLoadingBuckets(true);
    try {
      const metaRes = await getProfilerMetadata({
        itemsPerPage: 1,
        entities: [activeNode.value],
      });

      const metaRow = metaRes?.data?.data?.[0] ?? metaRes?.data?.[0] ?? null;
      const intervals = metaRow?.metadata?.corpora?.time_intervals ?? [];

      setTimestampBuckets(intervals);

      // Auto-select last bucket
      const current = activeTimestampBucket;
      const isCurrentValid = current && intervals.includes(current);

      if (isCurrentValid) {
        setActiveTimestampBucket(current);
      } else {
        const last = intervals.length ? intervals[intervals.length - 1] : null;
        setActiveTimestampBucket(last);
      }
    } catch (err) {
      console.error('Error loading timestamp buckets:', err);
    } finally {
      setLoadingBuckets(false);
    }
  }, [activeNode, activeTimestampBucket]);

  // Fetch data function for ParquetTable
  const fetchTableData = useCallback(async () => {
    return await getRiskProfile({
      entity_uuid: activeNode.value,
      stats_type: statsType,
      ...(activeTimestampBucket && { timestamp: activeTimestampBucket })
    });
  }, [activeNode, statsType, activeTimestampBucket]);

  // Load buckets when node changes
  useEffect(() => {
    if (!activeNode?.value) {
      setTimestampBuckets([]);
      setActiveTimestampBucket(null);
      return;
    }

    loadTimestampBuckets();
  }, [activeNode, loadTimestampBuckets]);

  // Reset filters
  const resetFiltersUI = () => {
    resetState("profileCompareState", [
      { stateFunc: setActiveNode, nullVal: null },
      { stateFunc: setActiveTimestampBucket, nullVal: null },
    ]);
    setTimestampBuckets([]);
    setStatsType('n_stats');
    setTableKey(prev => prev + 1);
  };

  // localStorage
  useEffect(() => {
    loadFromState("profileCompareState", [
      {
        stateFunc: setActiveNode,
        storageObjectKey: "activeNode",
        defaultVal: null,
      },
      {
        stateFunc: setActiveTimestampBucket,
        storageObjectKey: "activeTimestampBucket",
        defaultVal: null,
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => propagnateDataFunctionCopy && propagnateDataFunctionCopy({});
  }, []);

  useEffect(() => {
    if (activeNode !== null) {
      saveToState("profileCompareState", {
        activeNode,
        activeTimestampBucket,
      });
    }
  }, [activeNode, activeTimestampBucket]);

  const timestampOptions = useMemo(() => {
    return (timestampBuckets ?? []).map((tb) => ({
      label: formateTimeRange(tb),
      value: tb,
    }));
  }, [timestampBuckets]);

  // Determine if table should load
  const shouldLoadTable = activeNode?.value;

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}

      {!isLoading && (
        <div className="w-full flex justify-center">
          <div className="w-full max-w-6xl flex flex-col gap-8">
            {/* ROW 1: Node (left) + Timestamp (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end">
              {/* Left: Node */}
              <div className="flex flex-col gap-2 items-start">
                <p>Node</p>
                <div className="w-[320px]">
                  <AsyncPaginate
                    value={activeNode}
                    onChange={(selected) => setActiveNode(selected)}
                    loadOptions={loadNodeOptions}
                    additional={nodeAdditional}
                    debounceTimeout={250}
                    cacheOptions
                    placeholder="Type to search…"
                    noOptionsMessage={({ inputValue }) =>
                      (inputValue || "").trim().length < 2
                        ? "Type to search"
                        : "No matches"
                    }
                  />
                </div>
              </div>

              {/* Right: Timestamp Bucket */}
              <div className="flex flex-col gap-2 items-start">
                <p>Timestamp Bucket</p>
                <div className="w-[320px]">
                  <Select
                    isDisabled={!activeNode || loadingBuckets}
                    isSearchable={false}
                    value={
                      activeTimestampBucket
                        ? {
                            label: formateTimeRange(activeTimestampBucket),
                            value: activeTimestampBucket,
                          }
                        : null
                    }
                    onChange={(selected) =>
                      setActiveTimestampBucket(selected?.value ?? null)
                    }
                    options={timestampOptions}
                    placeholder={
                      !activeNode
                        ? "Select a node first…"
                        : loadingBuckets
                        ? "Loading buckets…"
                        : "Select…"
                    }
                  />
                </div>
              </div>
            </div>

            {/* ROW 2: Reset under Node (left only) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="flex items-start">
                <button
                  onClick={resetFiltersUI}
                  className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
                >
                  Reset filters
                </button>
              </div>
              <div />
            </div>

            {/* No Data Message */}
            {!activeNode && (
              <p className="text-sm opacity-70">
                Select a node to view risk profile data.
              </p>
            )}

            {/* Stats Type Buttons - Above Table */}
            {activeNode && (
              <div className="flex justify-center gap-4 mb-4">
                <button
                  onClick={() => setStatsType('n_stats')}
                  className={`px-4 py-2 rounded font-semibold transition duration-200 ${
                    statsType === 'n_stats'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-500 text-white hover:bg-gray-600'
                  }`}
                >
                  Node Stats
                </button>
                <button
                  onClick={() => setStatsType('ns_stats')}
                  className={`px-4 py-2 rounded font-semibold transition duration-200 ${
                    statsType === 'ns_stats'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-500 text-white hover:bg-gray-600'
                  }`}
                >
                  NodeSet Stats
                </button>
              </div>
            )}

            {/* Table */}
            <ParquetTable
              key={`${tableKey}-${statsType}`}
              fetchData={fetchTableData}
              headerNameMap={HEADER_NAME_MAP}
              columnsToHide={COLUMNS_TO_HIDE}
              defaultSortedColumn={DEFAULT_SORTED_COLUMN}
              shouldLoad={shouldLoadTable}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskProfile2oFilters;